import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Card, Etiqueta, Meta, Pilula, cores } from "../../relatorio/styles";
import { desdeQuando } from "../pages/estados";
import { situacaoDoPedido } from "../pages/acesso";
import { iniciais } from "../pages/contas";
import { LinkDeAcesso } from "./link-de-acesso";
import type { ClienteNovo as DadosDoClienteNovo, ContaConectada, PedidoDeAcesso, PerfilNoGoogle } from "../types";

const Painel = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  border: 1px solid ${cores.line};
`;

const Etapas = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
  @media (min-width: 1000px) {
    grid-template-columns: 1.2fr 1fr;
  }
`;

const Etapa = styled.div<{ $fundo: string }>`
  background: ${({ $fundo }) => $fundo};
  border-radius: 18px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  .cabeca {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 14px;
  }
  .numero {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: ${fonts.gilroyBold}, sans-serif;
    font-size: 13px;
  }
  input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${cores.line};
    border-radius: 1000px;
    padding: 11px 16px;
    font: inherit;
    font-size: 14px;
    outline: none;
    background: #fff;
  }
`;

const Resultado = styled.button<{ $ativo?: boolean }>`
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.blue : cores.line)};
  background: ${({ $ativo }) => ($ativo ? cores.blueTint : "#fff")};
  border-radius: 14px;
  padding: 10px 14px;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:hover {
    border-color: ${cores.blue};
  }
  .nome {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 14px;
  }
`;

const Conta = styled.button<{ $ativo?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.blue : cores.line)};
  background: ${({ $ativo }) => ($ativo ? cores.blueTint : "#fff")};
  border-radius: 14px;
  padding: 8px 12px;
  font: inherit;
  cursor: pointer;
  min-width: 0;
  &:hover {
    border-color: ${cores.blue};
  }
  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: ${cores.greenTint};
    color: ${cores.green};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 11px;
    flex-shrink: 0;
  }
  .email {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
`;

const Alternar = styled.button`
  align-self: flex-start;
  border: 0;
  background: none;
  color: ${cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
`;

const ItemDePedido = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${cores.line};
  font-size: 14px;
  &:first-child {
    border-top: 0;
  }
`;

type Props = {
  contas: ContaConectada[];
  onBuscarPlaces: (texto: string) => Promise<{ lista: PerfilNoGoogle[]; erro: string | null }>;
  onGerarLink: (cliente: DadosDoClienteNovo) => Promise<PedidoDeAcesso | null>;
  onListarPedidos: () => Promise<PedidoDeAcesso[]>;
};

/**
 * Cliente novo: a equipe acha o perfil no Google Places, escolhe qual conta
 * gestora recebe o acesso e gera o mesmo link que o cliente abre para liberar.
 */
export const ClienteNovo = ({ contas, onBuscarPlaces, onGerarLink, onListarPedidos }: Props) => {
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState<PerfilNoGoogle[]>([]);
  const [erroDaBusca, setErroDaBusca] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [perfil, setPerfil] = useState<PerfilNoGoogle | null>(null);
  const [manual, setManual] = useState(false);
  const [nomeManual, setNomeManual] = useState("");
  const [enderecoManual, setEnderecoManual] = useState("");
  const [filtroDeConta, setFiltroDeConta] = useState("");
  const [conta, setConta] = useState<ContaConectada | null>(null);
  const [gerando, setGerando] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoDeAcesso[]>([]);
  const [ultimo, setUltimo] = useState<PedidoDeAcesso | null>(null);
  const temporizador = useRef<number | null>(null);

  useEffect(() => {
    onListarPedidos().then(setPedidos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (temporizador.current) window.clearTimeout(temporizador.current);
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados([]);
      return undefined;
    }
    temporizador.current = window.setTimeout(async () => {
      setBuscando(true);
      const resposta = await onBuscarPlaces(termo);
      setResultados(resposta.lista);
      setErroDaBusca(resposta.erro);
      setBuscando(false);
    }, 450);
    return () => {
      if (temporizador.current) window.clearTimeout(temporizador.current);
    };
  }, [busca, onBuscarPlaces]);

  // Só contas que já conectaram ao Google podem aceitar o convite sozinhas.
  const contasElegiveis = useMemo(() => {
    const termo = filtroDeConta.trim().toLowerCase();
    return contas
      .filter((c) => c.hasToken && c.email)
      .filter((c) => !termo || c.email.toLowerCase().includes(termo) || (c.name ?? "").toLowerCase().includes(termo))
      .sort((a, b) => Number(b.connected) - Number(a.connected) || a.email.localeCompare(b.email));
  }, [contas, filtroDeConta]);

  const perfilPronto = manual ? nomeManual.trim().length >= 3 : perfil !== null;
  const nomeEscolhido = manual ? nomeManual.trim() : (perfil?.displayName ?? "");

  const gerar = async () => {
    if (!perfilPronto || !conta) return;
    setGerando(true);
    const dados: DadosDoClienteNovo = manual || !perfil
      ? { placeId: null, name: nomeManual.trim(), address: enderecoManual.trim(), managerUserId: conta.userId }
      : { placeId: perfil.profileId, name: perfil.displayName, address: perfil.formattedAddress, managerUserId: conta.userId };
    const pedido = await onGerarLink(dados);
    setGerando(false);
    if (pedido) {
      setUltimo(pedido);
      setPedidos((atual) => [pedido, ...atual.filter((p) => p.token !== pedido.token)]);
    }
  };

  const recomecar = () => {
    setUltimo(null);
    setPerfil(null);
    setConta(null);
    setBusca("");
    setResultados([]);
    setManual(false);
    setNomeManual("");
    setEnderecoManual("");
  };

  return (
    <Painel aria-label="Pegar acesso de cliente novo">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16 }}>Pegar acesso de cliente novo</span>
          <Meta style={{ display: "block", marginTop: 2 }}>Ache o perfil no Google, escolha o e-mail gestor que vai receber o acesso e mande o link para o cliente.</Meta>
        </div>
        {ultimo && (
          <Pilula as="button" type="button" $tipo="contorno" onClick={recomecar} style={{ padding: "8px 16px", fontSize: 13 }}>
            Outro cliente
          </Pilula>
        )}
      </div>

      {ultimo ? (
        <Card $fundo={cores.greenTint} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15 }}>{`Link pronto para “${ultimo.profileName}”`}</span>
          <Meta>{`Acesso vai para ${ultimo.managerEmail} · vale 7 dias`}</Meta>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <code style={{ fontSize: 12, color: cores.ink3, overflowWrap: "anywhere" }}>{ultimo.link}</code>
            <LinkDeAcesso pedido={ultimo} gerando={false} onGerar={gerar} />
          </div>
        </Card>
      ) : (
        <>
          <Etapas>
            <Etapa $fundo={cores.blueTint}>
              <span className="cabeca">
                <span className="numero" style={{ background: "#1A73E8" }}>1</span>
                Qual é o perfil no Google?
              </span>
              <input
                type="search"
                placeholder="Nome da empresa e cidade (ex.: Clínica Sorriso Itaúna)"
                aria-label="Buscar perfil no Google"
                value={busca}
                onChange={(evento) => {
                  setBusca(evento.target.value);
                  setPerfil(null);
                }}
              />
              {buscando && <Meta>Buscando no Google…</Meta>}
              {!buscando && erroDaBusca && <Meta style={{ color: cores.red }}>{`A busca no Google falhou: ${erroDaBusca}`}</Meta>}
              {!buscando && !erroDaBusca && busca.trim().length >= 3 && resultados.length === 0 && <Meta>Nenhum perfil encontrado. Tente nome e cidade.</Meta>}
              {resultados.length > 0 && (
                <Lista>
                  {resultados.map((item) => (
                    <Resultado key={item.profileId} type="button" $ativo={perfil?.profileId === item.profileId} onClick={() => setPerfil(item)}>
                      <span className="nome">{item.displayName}</span>
                      <Meta>{item.formattedAddress}</Meta>
                    </Resultado>
                  ))}
                </Lista>
              )}
              {perfil && !manual && (
                <div>
                  <Etiqueta $cor="azul">{`Escolhido: ${perfil.displayName}`}</Etiqueta>
                </div>
              )}
              <Alternar type="button" onClick={() => setManual((atual) => !atual)}>
                {manual ? "Voltar para a busca no Google" : "Não achou? Digitar o nome do perfil"}
              </Alternar>
              {manual && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input type="text" placeholder="Nome do perfil como está no Google" aria-label="Nome do perfil" value={nomeManual} onChange={(evento) => setNomeManual(evento.target.value)} />
                  <input type="text" placeholder="Cidade/UF (opcional)" aria-label="Cidade do perfil" value={enderecoManual} onChange={(evento) => setEnderecoManual(evento.target.value)} />
                  <Meta>Sem o perfil do Google, o cliente escolhe a ficha certa na lista dele ao entrar com o Google.</Meta>
                </div>
              )}
            </Etapa>

            <Etapa $fundo={cores.greenTint}>
              <span className="cabeca">
                <span className="numero" style={{ background: "#188038" }}>2</span>
                Para qual e-mail gestor vai o acesso?
              </span>
              <input
                type="search"
                placeholder="Filtrar contas gestoras"
                aria-label="Filtrar contas gestoras"
                value={filtroDeConta}
                onChange={(evento) => setFiltroDeConta(evento.target.value)}
              />
              <Lista>
                {contasElegiveis.map((item) => (
                  <Conta key={item.userId} type="button" $ativo={conta?.userId === item.userId} onClick={() => setConta(item)}>
                    <span className="avatar">{iniciais(item.name, item.email)}</span>
                    <span className="email">{item.email}</span>
                    <Etiqueta $cor={item.connected ? "verde" : "vermelha"}>{item.connected ? "Conectada" : "Reconectar"}</Etiqueta>
                  </Conta>
                ))}
                {contasElegiveis.length === 0 && <Meta>Nenhuma conta gestora conectada com esse filtro.</Meta>}
              </Lista>
            </Etapa>
          </Etapas>

          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <Pilula as="button" type="button" onClick={gerar} disabled={!perfilPronto || !conta || gerando} style={{ padding: "11px 22px", fontSize: 14, opacity: !perfilPronto || !conta ? 0.55 : 1 }}>
              {gerando ? "Gerando…" : "Gerar link para o cliente"}
            </Pilula>
            <Meta>
              {perfilPronto && conta
                ? `“${nomeEscolhido}” → ${conta.email}`
                : "Escolha o perfil e a conta gestora para liberar o botão."}
            </Meta>
          </div>
        </>
      )}

      {pedidos.length > 0 && (
        <div>
          <Meta style={{ display: "block", marginBottom: 4, fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>Links de clientes novos</Meta>
          <div>
            {pedidos.slice(0, 12).map((pedido) => {
              const situacao = situacaoDoPedido(pedido);
              return (
                <ItemDePedido key={pedido.token}>
                  <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: fonts.gilroySemiBold }}>{pedido.profileName}</span>
                    <Meta>{`${pedido.managerEmail} · gerado ${desdeQuando(pedido.createdAt)}${pedido.customerEmail ? ` · cliente ${pedido.customerEmail}` : ""}`}</Meta>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
                    {pedido.status !== "CONCLUIDO" && (
                      <button
                        type="button"
                        onClick={() => setUltimo(pedido)}
                        style={{ border: `1px solid ${cores.line}`, background: "#fff", color: cores.blue, borderRadius: 1000, padding: "6px 12px", font: "inherit", fontSize: 12, cursor: "pointer" }}
                      >
                        Ver link
                      </button>
                    )}
                  </span>
                </ItemDePedido>
              );
            })}
          </div>
        </div>
      )}
    </Painel>
  );
};
